import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { CITATION_SOURCE_SLUGS } from './citation-sources';

const c = initContract();

export const ddxCitationSchema = z.object({
  quote: z.string(),
  sourceSlug: z.enum(CITATION_SOURCE_SLUGS),
  sourceLabel: z.string(),
  artifactId: z.string().nullable(),
  /**
   * Offsets into this appointment's transcript, recomputed server-side. Null
   * when the quote could not be located — render it without a jump affordance
   * rather than anchor to a passage that may be the wrong one.
   */
  span: z.object({ start: z.number().int(), end: z.number().int() }).nullable(),
  /**
   * Transcript text around `quote`. Clients must not slice their own copy:
   * `span` indexes the string the server used (diarized when available), which
   * is not always the one the transcript tab renders. Null whenever `span` is.
   */
  context: z.object({ before: z.string(), after: z.string() }).nullable(),
});

export const ddxEvidenceCitationSchema = ddxCitationSchema.extend({
  /** The range of the evidence bullet's own `text` that this passage backs. */
  evidenceSpan: z.object({
    start: z.number().int(),
    end: z.number().int(),
  }),
});

export const ddxEvidenceSchema = z.object({
  text: z.string(),
  /** Ordered and non-overlapping, so clients can splice them in one pass. */
  citations: z.array(ddxEvidenceCitationSchema),
});

export const ddxDiagnosisSchema = z.object({
  candidateDiagnosis: z.string(),
  signalStrength: z.enum(['strong', 'moderate', 'weak']),
  clinicalUrgency: z.enum(['strong', 'moderate', 'weak']),
  supportingEvidence: z.array(ddxEvidenceSchema),
  currentDifferentialGap: z.string(),
  keyUncertainty: z.string(),
  suggestedNextSteps: z.string(),
  /** Citations that could not be attributed to a single evidence bullet. */
  sources: z.array(ddxCitationSchema),
});

export const differentialDiagnosesViewModelSchema = z.object({
  llmOutputId: z.string(),
  completedAt: z.string().nullable(),
  diagnoses: z.array(ddxDiagnosisSchema),
});

export type DdxCitation = z.infer<typeof ddxCitationSchema>;
export type DdxEvidenceCitation = z.infer<typeof ddxEvidenceCitationSchema>;
export type DdxEvidence = z.infer<typeof ddxEvidenceSchema>;
export type DdxDiagnosis = z.infer<typeof ddxDiagnosisSchema>;
export type DifferentialDiagnosesViewModel = z.infer<
  typeof differentialDiagnosesViewModelSchema
>;

export const differentialDiagnosesContract = c.router(
  {
    getViewModel: {
      method: 'GET',
      // appointmentId is a path param so AppointmentAccessGuard can read it.
      path: '/:appointmentId',
      pathParams: z.object({
        tenantId: z.string(),
        appointmentId: z.string(),
      }),
      responses: {
        200: differentialDiagnosesViewModelSchema,
        404: z.object({ message: z.string() }),
      },
      summary:
        'Latest differential diagnoses for an appointment, assembled into a display-ready view model: quotes deduplicated and located against the live transcript. Assembled at read time, so outputs generated before this endpoint existed are served too.',
    },
  },
  { pathPrefix: '/:tenantId/differential-diagnoses' },
);
